import type {
  AccessGrantDiff,
  AccessResourceType,
} from '~/features/access-control/types/AccessControl';

import { showToast } from '~/eds/src/components/Toast/showToast';
import { updateMockResourceAccess } from '~/features/access-control/mocks/accessControlMockStore';
import { withAccessControlMockFallback } from '~/features/access-control/mocks/withAccessControlMockFallback';
import { ServiceRequestConfig } from '~/services/createService';
import { pilot } from '~/services/serviceInstances';

import type {
  ResourceAccess,
  ServerAccessGrant,
  ServerAccessPrincipalRef,
  ServerResourceAccess,
} from './accessGrantMapper';

import {
  mapGrantToServer,
  mapPrincipalToServerRef,
  mapResourceAccessResponse,
} from './accessGrantMapper';

type Request = {
  resourceId: string;
  resourceType: AccessResourceType;
} & AccessGrantDiff;

type ServerRequest = {
  resource_id: string;
  resource_type: AccessResourceType;
  inherit_from_parent: boolean;
  added_grants: ServerAccessGrant[];
  updated_grants: ServerAccessGrant[];
  removed_principals: ServerAccessPrincipalRef[];
};

export const updateResourceAccess = async (
  request: Request,
  options: ServiceRequestConfig = {},
): Promise<ResourceAccess> => {
  return withAccessControlMockFallback(
    async () => {
      const serverResponse = await pilot.put<ServerResourceAccess>(
        '/access/resource/',
        mapRequest(request),
        options,
      );

      return mapResourceAccessResponse(serverResponse);
    },
    () => {
      const result = updateMockResourceAccess(
        request.resourceType,
        request.resourceId,
        {
          inheritFromParent: request.inheritFromParent,
          addedGrants: request.addedGrants,
          updatedGrants: request.updatedGrants,
          removedPrincipalKeys: request.removedPrincipals.map(
            (principal) => `${principal.type}:${principal.id}`,
          ),
        },
      );

      if (options.showSuccessToast) {
        showToast({
          status: 'success',
          message:
            typeof options.showSuccessToast === 'string'
              ? options.showSuccessToast
              : '',
        });
      }

      return result;
    },
  );
};

function mapRequest(request: Request): ServerRequest {
  return {
    resource_id: request.resourceId,
    resource_type: request.resourceType,
    inherit_from_parent: request.inheritFromParent,
    added_grants: request.addedGrants.map(mapGrantToServer),
    updated_grants: request.updatedGrants.map(mapGrantToServer),
    removed_principals: request.removedPrincipals.map(mapPrincipalToServerRef),
  };
}
